// Libs
import classNames from 'classnames/bind';
import {
  Button,
  Flex,
  Heading,
  IconButton,
  IconEditLine,
  IconEndLine,
  Modal,
  Spinner,
  Table,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseSelect } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { getStudentDiscussionForums, getStudentExams, getStudentVideoLectures } from '~/thunks/user/userThunk';
import { adminUpdateModuleWatchProgress } from '~/thunks/module/moduleThunk';
import {
  createDiscussionForumGrading,
  deleteDiscussionForumGrading,
  updateDiscussionForumGrading,
} from '~/thunks/discussionForumGrading/discussionForumGradingThunk';
import { createExam, deleteExamById, updateExam } from '~/thunks/exam/examThunk';
import { IDiscussionForumProgress, IExamProgress, IVideoLectureProgress } from '~/utils/interface/studentProgress';
import { ExamStatusEnum } from '~/utils/enum/base';
import { DEFAULT_PAGE } from '~/utils/constants/common';
// Styles, images, icons
import styles from './StudentDetail.module.scss';
import { icons } from '~/assets';

type LocationState = {
  studentName?: string;
  studentEmail?: string;
};

type ExamFormState = {
  status: ExamStatusEnum;
  pointValue: string;
  value: string;
  credits: string;
};

type ColumnState<T> = {
  items: T[];
  page: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
};

const initialColumnState = <T,>(): ColumnState<T> => ({
  items: [],
  page: DEFAULT_PAGE,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
});

const cx = classNames.bind(styles);

const StudentDetail = () => {
  //#region Declare Hook
  const { t } = useTranslation(['common/base', 'admin/studentDetail', 'admin/administrativeArea']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { studentId } = useParams();
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  //#endregion Declare Hook

  //#region Declare State
  const [courseId, setCourseId] = useState<string>();
  const [courseName, setCourseName] = useState<string>();

  const [videoLectures, setVideoLectures] = useState<ColumnState<IVideoLectureProgress>>(initialColumnState());
  const [discussionForums, setDiscussionForums] = useState<ColumnState<IDiscussionForumProgress>>(
    initialColumnState()
  );
  const [exams, setExams] = useState<ColumnState<IExamProgress>>(initialColumnState());

  const [videoRowLoadingId, setVideoRowLoadingId] = useState<string>();
  const [editingDiscussion, setEditingDiscussion] = useState<IDiscussionForumProgress>();
  const [discussionGradingPoint, setDiscussionGradingPoint] = useState<string>('');
  const [discussionGradingPointError, setDiscussionGradingPointError] = useState<string>();
  const [discussionAction, setDiscussionAction] = useState<'save' | 'remove'>();
  const [editingExam, setEditingExam] = useState<IExamProgress>();
  const [examAction, setExamAction] = useState<'save' | 'remove'>();
  const [examForm, setExamForm] = useState<ExamFormState>({
    status: ExamStatusEnum.PASSED,
    pointValue: '',
    value: '',
    credits: '',
  });
  const [examFormErrors, setExamFormErrors] = useState<Partial<ExamFormState>>({});
  //#endregion Declare State

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const handleFetchVideoLectures = useCallback(
    async (page: number) => {
      if (!studentId) return;
      setVideoLectures((prev) => ({
        ...prev,
        isLoading: page === DEFAULT_PAGE,
        isLoadingMore: page !== DEFAULT_PAGE,
      }));
      try {
        const resp = await dispatch(getStudentVideoLectures({ studentId, page })).unwrap();
        const data = resp.data;
        setCourseId(data?.courseId);
        setCourseName(data?.courseName);
        setVideoLectures((prev) => ({
          items: page === DEFAULT_PAGE ? data?.responses ?? [] : [...prev.items, ...(data?.responses ?? [])],
          page: data?.currentPage ?? page,
          hasMore: data?.hasMore ?? false,
          isLoading: false,
          isLoadingMore: false,
        }));
      } catch {
        setVideoLectures((prev) => ({ ...prev, isLoading: false, isLoadingMore: false }));
      }
    },
    [dispatch, studentId]
  );

  const handleFetchDiscussionForums = useCallback(
    async (page: number) => {
      if (!studentId) return;
      setDiscussionForums((prev) => ({
        ...prev,
        isLoading: page === DEFAULT_PAGE,
        isLoadingMore: page !== DEFAULT_PAGE,
      }));
      try {
        const resp = await dispatch(getStudentDiscussionForums({ studentId, page })).unwrap();
        const data = resp.data;
        setCourseId(data?.courseId);
        setCourseName(data?.courseName);
        setDiscussionForums((prev) => ({
          items: page === DEFAULT_PAGE ? data?.responses ?? [] : [...prev.items, ...(data?.responses ?? [])],
          page: data?.currentPage ?? page,
          hasMore: data?.hasMore ?? false,
          isLoading: false,
          isLoadingMore: false,
        }));
      } catch {
        setDiscussionForums((prev) => ({ ...prev, isLoading: false, isLoadingMore: false }));
      }
    },
    [dispatch, studentId]
  );

  const handleFetchExams = useCallback(
    async (page: number) => {
      if (!studentId) return;
      setExams((prev) => ({ ...prev, isLoading: page === DEFAULT_PAGE, isLoadingMore: page !== DEFAULT_PAGE }));
      try {
        const resp = await dispatch(getStudentExams({ studentId, page })).unwrap();
        const data = resp.data;
        setCourseId(data?.courseId);
        setCourseName(data?.courseName);
        setExams((prev) => ({
          items: page === DEFAULT_PAGE ? data?.responses ?? [] : [...prev.items, ...(data?.responses ?? [])],
          page: data?.currentPage ?? page,
          hasMore: data?.hasMore ?? false,
          isLoading: false,
          isLoadingMore: false,
        }));
      } catch {
        setExams((prev) => ({ ...prev, isLoading: false, isLoadingMore: false }));
      }
    },
    [dispatch, studentId]
  );

  useEffect(() => {
    handleFetchVideoLectures(DEFAULT_PAGE);
    handleFetchDiscussionForums(DEFAULT_PAGE);
    handleFetchExams(DEFAULT_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleToggleVideoWatched = async (item: IVideoLectureProgress) => {
    if (!studentId || !courseId) return;
    setVideoRowLoadingId(item.moduleId);
    try {
      await dispatch(
        adminUpdateModuleWatchProgress({
          moduleId: item.moduleId,
          body: { studentId, courseId, isWatched: !item.watched },
        })
      ).unwrap();
      setVideoLectures((prev) => ({
        ...prev,
        items: prev.items.map((lecture) =>
          lecture.moduleId === item.moduleId ? { ...lecture, watched: !item.watched } : lecture
        ),
      }));
    } finally {
      setVideoRowLoadingId(undefined);
    }
  };

  const handleOpenDiscussionModal = (item: IDiscussionForumProgress) => {
    setEditingDiscussion(item);
    setDiscussionGradingPoint(item.gradingPoint !== undefined ? String(item.gradingPoint) : '');
    setDiscussionGradingPointError(undefined);
  };

  const handleCloseDiscussionModal = () => {
    if (discussionAction) return;
    setEditingDiscussion(undefined);
    setDiscussionGradingPointError(undefined);
  };

  const handleBlurDiscussionGradingPoint = () => {
    const value = discussionGradingPoint.trim();
    if (!value) return;

    const numberValue = parseFloat(value);
    const isValid = isFinite(numberValue) && numberValue >= 1 && numberValue <= 5;
    setDiscussionGradingPoint(isValid ? numberValue.toString() : '');
  };

  const handleSaveDiscussion = async () => {
    if (!studentId || !courseId || !editingDiscussion) return;

    if (!discussionGradingPoint.trim()) {
      setDiscussionGradingPointError(t('common_validate_field_required', { ns: 'common/base' }));
      return;
    }

    setDiscussionAction('save');
    try {
      const gradingPoint = Number(discussionGradingPoint);
      const discussionId = editingDiscussion.discussionId;
      let gradeId = editingDiscussion.gradeId;

      if (editingDiscussion.completed && editingDiscussion.gradeId) {
        const res = await dispatch(
          updateDiscussionForumGrading({
            gradingId: editingDiscussion.gradeId,
            body: { gradingPoint },
          })
        ).unwrap();
        gradeId = res.data?.id ?? gradeId;
      } else {
        const res = await dispatch(
          createDiscussionForumGrading({
            studentId: +studentId,
            studentName: state.studentName ?? '',
            courseId: +courseId,
            courseName: courseName ?? '',
            discussionId: +editingDiscussion.discussionId,
            discussionName: editingDiscussion.discussionName,
            gradingPoint,
          })
        ).unwrap();
        gradeId = res.data?.id;
      }

      setDiscussionForums((prev) => ({
        ...prev,
        items: prev.items.map((discussion) =>
          discussion.discussionId === discussionId ? { ...discussion, completed: true, gradeId, gradingPoint } : discussion
        ),
      }));
      setEditingDiscussion(undefined);
    } finally {
      setDiscussionAction(undefined);
    }
  };

  const handleRemoveDiscussionGrade = async () => {
    if (!editingDiscussion?.gradeId) return;
    const discussionId = editingDiscussion.discussionId;
    setDiscussionAction('remove');
    try {
      await dispatch(deleteDiscussionForumGrading(editingDiscussion.gradeId)).unwrap();
      setDiscussionForums((prev) => ({
        ...prev,
        items: prev.items.map((discussion) =>
          discussion.discussionId === discussionId
            ? { ...discussion, completed: false, gradeId: undefined, gradingPoint: undefined }
            : discussion
        ),
      }));
      setEditingDiscussion(undefined);
    } finally {
      setDiscussionAction(undefined);
    }
  };

  const handleOpenExamModal = (item: IExamProgress) => {
    setEditingExam(item);
    setExamForm({
      status: (item.status as ExamStatusEnum) ?? ExamStatusEnum.PASSED,
      pointValue: item.pointValue !== undefined ? String(item.pointValue) : '',
      value: item.value ?? '',
      credits: item.credits !== undefined ? String(item.credits) : '',
    });
    setExamFormErrors({});
  };

  const handleCloseExamModal = () => {
    if (examAction) return;
    setEditingExam(undefined);
  };

  const handleBlurExamPointValue = () => {
    const value = examForm.pointValue.trim();
    if (!value) return;

    const numberValue = parseFloat(value);
    setExamForm((prev) => ({ ...prev, pointValue: isFinite(numberValue) ? numberValue.toString() : '' }));
  };

  const handleBlurExamCredits = () => {
    const value = examForm.credits.trim();
    if (!value) return;

    const numberValue = parseFloat(value);
    setExamForm((prev) => ({ ...prev, credits: isFinite(numberValue) ? numberValue.toString() : '' }));
  };

  const validateExamForm = () => {
    const requiredMessage = t('common_validate_field_required', { ns: 'common/base' });
    const errors: Partial<ExamFormState> = {};
    if (!examForm.value.trim()) errors.value = requiredMessage;
    if (!examForm.pointValue.trim()) errors.pointValue = requiredMessage;
    if (!examForm.credits.trim()) errors.credits = requiredMessage;
    setExamFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveExam = async () => {
    if (!studentId || !courseId || !editingExam) return;
    if (!validateExamForm()) return;

    setExamAction('save');
    try {
      const moduleId = editingExam.moduleId;
      const pointValue = Number(examForm.pointValue);
      const credits = Number(examForm.credits);
      let examRecordId = editingExam.examId;

      if (editingExam.taken && editingExam.examId) {
        const res = await dispatch(
          updateExam({
            examId: editingExam.examId,
            body: {
              status: examForm.status,
              pointValue,
              value: examForm.value,
              credits,
            },
          })
        ).unwrap();
        examRecordId = res.data?.id ?? examRecordId;
      } else {
        const res = await dispatch(
          createExam({
            studentId,
            studentName: state.studentName ?? '',
            courseId,
            courseName: courseName ?? '',
            moduleId: editingExam.moduleId,
            moduleName: editingExam.moduleName,
            status: examForm.status,
            pointValue,
            value: examForm.value,
            credits,
          })
        ).unwrap();
        examRecordId = res.data?.id;
      }

      setExams((prev) => ({
        ...prev,
        items: prev.items.map((exam) =>
          exam.moduleId === moduleId
            ? {
                ...exam,
                taken: true,
                examId: examRecordId,
                status: examForm.status,
                pointValue,
                value: examForm.value,
                credits,
              }
            : exam
        ),
      }));
      setEditingExam(undefined);
    } finally {
      setExamAction(undefined);
    }
  };

  const handleRemoveExam = async () => {
    if (!editingExam?.examId) return;
    const moduleId = editingExam.moduleId;
    setExamAction('remove');
    try {
      await dispatch(deleteExamById(editingExam.examId)).unwrap();
      setExams((prev) => ({
        ...prev,
        items: prev.items.map((exam) =>
          exam.moduleId === moduleId
            ? {
                ...exam,
                taken: false,
                examId: undefined,
                status: undefined,
                pointValue: undefined,
                value: undefined,
                credits: undefined,
              }
            : exam
        ),
      }));
      setEditingExam(undefined);
    } finally {
      setExamAction(undefined);
    }
  };
  //#endregion Handle Function

  const statusOptions = [
    { value: ExamStatusEnum.PASSED, label: t('administrative_area_pass_status', { ns: 'admin/administrativeArea' }) },
    { value: ExamStatusEnum.FAILED, label: t('administrative_area_fail_status', { ns: 'admin/administrativeArea' }) },
  ];

  const renderEmpty = () => (
    <div className={cx('emptyContainer')}>
      <Text variant='contentSmall'>{t('student_detail_no_data', { ns: 'admin/studentDetail' })}</Text>
    </div>
  );

  const renderLoadMore = (hasMore: boolean, isLoadingMore: boolean, onClick: () => void) => {
    if (!hasMore) return null;
    return (
      <Flex justifyItems='center' margin='small 0 0 0'>
        <Button onClick={onClick} disabled={isLoadingMore} color='primary-inverse'>
          {isLoadingMore
            ? t('loading_more_button_label', { ns: 'admin/studentDetail' })
            : t('load_more_button_label', { ns: 'admin/studentDetail' })}
        </Button>
      </Flex>
    );
  };

  const renderDiscussionForums = () => (
    <View as='div' className={cx('column')}>
      <Heading level='h4'>{t('student_detail_discussion_forums_column', { ns: 'admin/studentDetail' })}</Heading>
      {discussionForums.isLoading ? (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      ) : discussionForums.items.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='discussion-name'>
                  {t('student_detail_name_column', { ns: 'admin/studentDetail' })}
                </Table.ColHeader>
                <Table.ColHeader id='discussion-points' textAlign='center'>
                  {t('student_detail_grading_point_field_label', { ns: 'admin/studentDetail' })}
                </Table.ColHeader>
                <Table.ColHeader id='discussion-action' textAlign='center'>
                  {t('action_label', { ns: 'common/base' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {discussionForums.items.map((item) => (
                <Table.Row key={item.discussionId}>
                  <Table.Cell>
                    <span>{item.discussionName}</span>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    <Text variant='contentSmall' color={item.completed ? 'success' : 'secondary'}>
                      {item.completed ? item.gradingPoint ?? 0 : '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    <IconButton
                      withBorder={false}
                      withBackground={false}
                      color='primary'
                      size='small'
                      onClick={() => handleOpenDiscussionModal(item)}
                      screenReaderLabel='Edit'
                    >
                      <IconEditLine />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {renderLoadMore(discussionForums.hasMore, discussionForums.isLoadingMore, () =>
            handleFetchDiscussionForums(discussionForums.page + 1)
          )}
        </>
      )}
    </View>
  );

  const renderVideoLectures = () => (
    <View as='div' className={cx('column')}>
      <Heading level='h4'>{t('student_detail_video_lectures_column', { ns: 'admin/studentDetail' })}</Heading>
      {videoLectures.isLoading ? (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      ) : videoLectures.items.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='module-name'>
                  {t('student_detail_name_column', { ns: 'admin/studentDetail' })}
                </Table.ColHeader>
                <Table.ColHeader id='module-status' textAlign='center'>
                  {t('student_detail_status_field_label', { ns: 'admin/studentDetail' })}
                </Table.ColHeader>
                <Table.ColHeader id='module-action' textAlign='center'>
                  {t('action_label', { ns: 'common/base' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {videoLectures.items.map((item) => (
                <Table.Row key={item.moduleId}>
                  <Table.Cell>
                    <span>{item.moduleName}</span>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    <Text variant='contentSmall' color={item.watched ? 'success' : 'secondary'}>
                      {item.watched
                        ? t('student_detail_watched_status', { ns: 'admin/studentDetail' })
                        : t('student_detail_not_watched_status', { ns: 'admin/studentDetail' })}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    {videoRowLoadingId === item.moduleId ? (
                      <Spinner size='x-small' renderTitle={t('common_base_loading_label')} />
                    ) : (
                      <IconButton
                        withBorder={false}
                        withBackground={false}
                        color='primary'
                        size='small'
                        onClick={() => handleToggleVideoWatched(item)}
                        screenReaderLabel='Edit'
                      >
                        <IconEditLine />
                      </IconButton>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {renderLoadMore(videoLectures.hasMore, videoLectures.isLoadingMore, () =>
            handleFetchVideoLectures(videoLectures.page + 1)
          )}
        </>
      )}
    </View>
  );

  const renderExams = () => (
    <View as='div' className={cx('column')}>
      <Heading level='h4'>{t('student_detail_exams_column', { ns: 'admin/studentDetail' })}</Heading>
      {exams.isLoading ? (
        <div className={cx('spinnerContainer')}>
          <Spinner size='small' />
        </div>
      ) : exams.items.length === 0 ? (
        renderEmpty()
      ) : (
        <>
          <Table layout='auto'>
            <Table.Head>
              <Table.Row>
                <Table.ColHeader id='exam-name'>
                  {t('student_detail_name_column', { ns: 'admin/studentDetail' })}
                </Table.ColHeader>
                <Table.ColHeader id='exam-status' textAlign='center'>
                  {t('student_detail_status_field_label', { ns: 'admin/studentDetail' })}
                </Table.ColHeader>
                <Table.ColHeader id='exam-action' textAlign='center'>
                  {t('action_label', { ns: 'common/base' })}
                </Table.ColHeader>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {exams.items.map((item) => (
                <Table.Row key={item.moduleId}>
                  <Table.Cell>
                    <span>{item.moduleName}</span>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    <Text variant='contentSmall' color={item.taken ? 'success' : 'secondary'}>
                      {item.taken
                        ? item.status === ExamStatusEnum.FAILED
                          ? t('administrative_area_fail_status', { ns: 'admin/administrativeArea' })
                          : t('administrative_area_pass_status', { ns: 'admin/administrativeArea' })
                        : t('student_detail_not_taken_status', { ns: 'admin/studentDetail' })}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign='center'>
                    <IconButton
                      withBorder={false}
                      withBackground={false}
                      color='primary'
                      size='small'
                      onClick={() => handleOpenExamModal(item)}
                      screenReaderLabel='Edit'
                    >
                      <IconEditLine />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {renderLoadMore(exams.hasMore, exams.isLoadingMore, () => handleFetchExams(exams.page + 1))}
        </>
      )}
    </View>
  );

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('studentTitle')}>
          <div className={cx('studentHeaderTitle')}>
            <Heading level='h3'>{state.studentName ?? t('student_detail_title', { ns: 'admin/studentDetail' })}</Heading>
          </div>
        </View>

        <div className={cx('columnsContainer')}>
          {renderDiscussionForums()}
          {renderVideoLectures()}
          {renderExams()}
        </div>
      </div>

      <Modal open={!!editingDiscussion} onDismiss={handleCloseDiscussionModal} label='' size='small'>
        <Modal.Header frameBorder='none'>
          <View className={cx('modalHeader')}>
            <Heading level='h4'>{editingDiscussion?.discussionName}</Heading>
            <View as='div' onClick={handleCloseDiscussionModal} className={cx('iconCloseModal')}>
              <IconEndLine />
            </View>
          </View>
        </Modal.Header>
        <Modal.Body>
          <View as='div' padding='medium'>
            <TextInput
              renderLabel={t('student_detail_grading_point_field_label', { ns: 'admin/studentDetail' })}
              type='text'
              value={discussionGradingPoint}
              onChange={(_, value) => {
                setDiscussionGradingPoint(value);
                setDiscussionGradingPointError(undefined);
              }}
              onBlur={handleBlurDiscussionGradingPoint}
            />
            {discussionGradingPointError && (
              <View as='div' margin='xx-small 0 0 0'>
                <Text variant='legend' color='danger'>
                  {discussionGradingPointError}
                </Text>
              </View>
            )}
          </View>
        </Modal.Body>
        <Modal.Footer>
          <Flex justifyItems='end' gap='xx-small'>
            {editingDiscussion?.completed && (
              <Button color='danger' onClick={handleRemoveDiscussionGrade} disabled={!!discussionAction}>
                {discussionAction === 'remove'
                  ? t('deleting_button_label', { ns: 'common/base' })
                  : t('student_detail_not_completed_status', { ns: 'admin/studentDetail' })}
              </Button>
            )}
            <Button color='primary-inverse' onClick={handleCloseDiscussionModal} disabled={!!discussionAction}>
              {t('cancel_button_label', { ns: 'common/base' })}
            </Button>
            <Button color='primary' onClick={handleSaveDiscussion} disabled={!!discussionAction}>
              {discussionAction === 'save'
                ? t('saving_button_label', { ns: 'common/base' })
                : t('save_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </Modal.Footer>
      </Modal>

      <Modal open={!!editingExam} onDismiss={handleCloseExamModal} label='' size='small'>
        <Modal.Header frameBorder='none'>
          <View className={cx('modalHeader')}>
            <Heading level='h4'>{editingExam?.moduleName}</Heading>
            <View as='div' onClick={handleCloseExamModal} className={cx('iconCloseModal')}>
              <IconEndLine />
            </View>
          </View>
        </Modal.Header>
        <Modal.Body>
          <View as='div' padding='medium' className={cx('examForm')}>
            <BaseSelect
              label={t('student_detail_status_field_label', { ns: 'admin/studentDetail' })}
              value={examForm.status}
              options={statusOptions}
              onChange={(option) =>
                option && setExamForm((prev) => ({ ...prev, status: option.value as ExamStatusEnum }))
              }
            />
            <div>
              <TextInput
                renderLabel={t('student_detail_point_value_field_label', { ns: 'admin/studentDetail' })}
                type='text'
                value={examForm.pointValue}
                onChange={(_, value) => {
                  setExamForm((prev) => ({ ...prev, pointValue: value }));
                  setExamFormErrors((prev) => ({ ...prev, pointValue: undefined }));
                }}
                onBlur={handleBlurExamPointValue}
              />
              {examFormErrors.pointValue && (
                <View as='div' margin='xx-small 0 0 0'>
                  <Text variant='legend' color='danger'>
                    {examFormErrors.pointValue}
                  </Text>
                </View>
              )}
            </div>
            <div>
              <TextInput
                renderLabel={t('student_detail_value_field_label', { ns: 'admin/studentDetail' })}
                type='text'
                value={examForm.value}
                onChange={(_, value) => {
                  setExamForm((prev) => ({ ...prev, value }));
                  setExamFormErrors((prev) => ({ ...prev, value: undefined }));
                }}
              />
              {examFormErrors.value && (
                <View as='div' margin='xx-small 0 0 0'>
                  <Text variant='legend' color='danger'>
                    {examFormErrors.value}
                  </Text>
                </View>
              )}
            </div>
            <div>
              <TextInput
                renderLabel={t('student_detail_credits_field_label', { ns: 'admin/studentDetail' })}
                type='text'
                value={examForm.credits}
                onChange={(_, value) => {
                  setExamForm((prev) => ({ ...prev, credits: value }));
                  setExamFormErrors((prev) => ({ ...prev, credits: undefined }));
                }}
                onBlur={handleBlurExamCredits}
              />
              {examFormErrors.credits && (
                <View as='div' margin='xx-small 0 0 0'>
                  <Text variant='legend' color='danger'>
                    {examFormErrors.credits}
                  </Text>
                </View>
              )}
            </div>
          </View>
        </Modal.Body>
        <Modal.Footer>
          <Flex justifyItems='end' gap='xx-small'>
            {editingExam?.taken && (
              <Button color='danger' onClick={handleRemoveExam} disabled={!!examAction}>
                {examAction === 'remove'
                  ? t('deleting_button_label', { ns: 'common/base' })
                  : t('student_detail_not_taken_status', { ns: 'admin/studentDetail' })}
              </Button>
            )}
            <Button color='primary-inverse' onClick={handleCloseExamModal} disabled={!!examAction}>
              {t('cancel_button_label', { ns: 'common/base' })}
            </Button>
            <Button color='primary' onClick={handleSaveExam} disabled={!!examAction}>
              {examAction === 'save'
                ? t('saving_button_label', { ns: 'common/base' })
                : t('save_button_label', { ns: 'common/base' })}
            </Button>
          </Flex>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentDetail;
